import { useEffect, useRef, useState } from 'react';
import { ExternalLinkIcon, GlobeIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/app-public';
import { CompanyRequestError } from '@/company/api';
import { SessionChangedError } from '@/lib/session-guard';
import { useIntlLocale } from '../../intl-locale';
import { SheetButton, SheetNote, SheetSection } from '../../share-sheet/share-sheet';
import {
  publicLinkApi,
  readSites,
  type PublicLinkSelection,
  type PublicLinkState,
} from './public-link-api';
import './public-link.css';

type Copy = ReturnType<typeof useNamespaceCopy<keyof (typeof copyCatalog)['en']>>;
type Busy = 'publish' | 'refresh' | 'unpublish' | 'embed' | null;

function failureCopy(error: unknown, copy: Copy) {
  const code = error instanceof CompanyRequestError ? error.code : '';
  if (code === 'app_public_embed_origins_invalid') return copy.sitesInvalid;
  if (code === 'app_public_copy_too_large') return copy.copyTooLarge;
  if (code === 'app_public_rebuild_required' || code === 'app_public_build_unavailable')
    return copy.rebuild;
  // The App Check of this exact version (review H2).
  if (code === 'app_public_checking') return copy.checking;
  if (code === 'app_public_check_failed') return copy.checkFailed;
  if (code === 'app_public_version_changed') return copy.versionChanged;
  if (code === 'app_public_check_blocked')
    return (error as CompanyRequestError).reason === 'secret'
      ? copy.checkBlockedSecret
      : copy.checkBlockedDependency;
  return copy.failed;
}

function listOf(values: string[], locale: string) {
  try {
    return new Intl.ListFormat(locale, { type: 'conjunction' }).format(values);
  } catch {
    return values.join(', ');
  }
}

/** Review L11: the App functions a visitor's submission starts (its table's row events). */
function Runs({
  runs = [],
  copy,
  locale,
}: {
  runs?: { function: string; on: boolean }[];
  copy: Copy;
  locale: string;
}) {
  const names = (on: boolean) => [
    ...new Set(runs.filter((run) => run.on === on).map((run) => run.function)),
  ];
  const on = names(true);
  const off = names(false);
  if (!on.length && !off.length) return null;
  return (
    <span className="ws-public-runs" data-testid="public-link-action-runs">
      {[
        on.length ? formatCopy(copy.sendsRuns, { functions: listOf(on, locale) }) : '',
        off.length ? formatCopy(copy.sendsRunsOff, { functions: listOf(off, locale) }) : '',
      ]
        .filter(Boolean)
        .join(' ')}
    </span>
  );
}

/** What publishing makes public, in the owner's words: pages, copied tables, visitor actions. */
function Exposes({
  state,
  copy,
  titled = true,
}: {
  state: PublicLinkState;
  copy: Copy;
  titled?: boolean;
}) {
  const locale = useIntlLocale();
  const exposes = state.exposes ?? { collections: [], actions: [] };
  const table = (id: string, label: string | null) => label ?? id;
  return (
    <div className="ws-public-exposes" data-testid="public-link-exposes">
      {titled && <span className="ws-public-exposes-title">{copy.whatTitle}</span>}
      <ul>
        <li>{copy.pages}</li>
        {exposes.collections.map((item) => (
          <li key={item.id} data-testid="public-link-collection">
            {formatCopy(item.filtered ? copy.copyFiltered : copy.copyOf, {
              table: table(item.id, item.label),
              fields: listOf(item.fields, locale),
            })}
          </li>
        ))}
        {exposes.actions.map((item) => (
          <li key={item.id} data-testid="public-link-action">
            {formatCopy(copy.sends, {
              action: item.label ?? item.id,
              table: table(item.collectionId, item.collectionLabel),
            })}
            <Runs runs={item.runs} copy={copy} locale={locale} />
          </li>
        ))}
      </ul>
      {!exposes.collections.length && !exposes.actions.length && (
        <p className="ws-sharesheet-help">{copy.nothingDeclared}</p>
      )}
      <p className="ws-sharesheet-help">{copy.never}</p>
      {state.showMark && <p className="ws-sharesheet-help">{copy.mark}</p>}
    </div>
  );
}

function CopyField({
  value,
  label,
  copiedLabel,
  testId,
  multiline = false,
  children,
}: {
  value: string;
  label: string;
  copiedLabel: string;
  testId: string;
  multiline?: boolean;
  children?: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copyValue = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* The value stays selectable in the field. */
    }
  };
  return (
    <div className={`ws-public-field${multiline ? ' is-code' : ''}`}>
      {multiline ? (
        <code data-testid={testId}>{value}</code>
      ) : (
        <input readOnly value={value} aria-label={label} data-testid={testId} />
      )}
      <span className="ws-public-field-actions">
        {children}
        <SheetButton small onClick={() => void copyValue()} data-testid={`${testId}-copy`}>
          {copied ? copiedLabel : label}
        </SheetButton>
      </span>
    </div>
  );
}

/** Unpublish, asked once more before the link stops answering. */
function Unpublish({
  copy,
  busy,
  open,
  onOpen,
  onConfirm,
}: {
  copy: Copy;
  busy: Busy;
  open: boolean;
  onOpen: (open: boolean) => void;
  onConfirm: () => void;
}) {
  if (!open)
    return (
      <SheetButton
        small
        className="is-danger"
        disabled={busy !== null}
        data-testid="public-link-unpublish"
        onClick={() => onOpen(true)}
      >
        {copy.unpublish}
      </SheetButton>
    );
  return (
    <span className="ws-public-unpublish" role="group">
      <span>{copy.unpublishBody}</span>
      <SheetButton small disabled={busy !== null} onClick={() => onOpen(false)}>
        {copy.cancel}
      </SheetButton>
      <SheetButton
        small
        className="is-danger"
        disabled={busy !== null}
        data-testid="public-link-unpublish-confirm"
        onClick={onConfirm}
      >
        {busy === 'unpublish' ? copy.unpublishing : copy.unpublishConfirm}
      </SheetButton>
    </span>
  );
}

/**
 * The App's public link in its Share sheet. Owners publish (after confirming
 * what becomes public), copy the link and the embed code, refresh the copied
 * data and unpublish; anyone else sees the link only while it is live, and a
 * company owner or admin can unpublish it too (review M6).
 */
export function PublicLinkSection({
  selection,
  session,
}: {
  selection: PublicLinkSelection;
  session?: typeof fetch;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const locale = useIntlLocale();
  const [state, setState] = useState<PublicLinkState | null>(null);
  const [phase, setPhase] = useState<'loading' | 'failed' | 'ready'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<Busy>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [sites, setSites] = useState('');
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  useEffect(() => {
    let current = true;
    setPhase('loading');
    publicLinkApi.read(selection, session).then(
      (value) => {
        if (!current) return;
        setState(value);
        setSites(value.embedOrigins.join(', '));
        setPhase('ready');
      },
      (failure) => {
        if (current && !(failure instanceof SessionChangedError)) setPhase('failed');
      },
    );
    return () => {
      current = false;
    };
  }, [selection.artifactId, selection.versionId, selection.revisionId, session, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  async function run(kind: Exclude<Busy, null>, work: () => Promise<PublicLinkState>) {
    setBusy(kind);
    setError(null);
    try {
      const next = await work();
      if (!alive.current) return;
      setState((before) => ({
        ...next,
        // Unpublish and embed answers carry no declaration; keep what the sheet already shows.
        exposes: next.exposes ?? before?.exposes ?? null,
        selectedVersionId: before?.selectedVersionId,
      }));
      setSites(next.embedOrigins.join(', '));
      setConfirmed(false);
      setUnpublishing(false);
    } catch (failure) {
      if (alive.current && !(failure instanceof SessionChangedError))
        setError(failureCopy(failure, copy));
      // A refused refresh may have taken the link down: read the state again.
      if (alive.current && kind === 'refresh' && !(failure instanceof SessionChangedError))
        setAttempt((value) => value + 1);
    } finally {
      if (alive.current) setBusy(null);
    }
  }

  if (phase === 'loading')
    return (
      <SheetSection title={copy.title} testId="public-link">
        <div className="ws-public" aria-busy="true">
          <div className="ws-public-skeleton" />
        </div>
      </SheetSection>
    );
  if (phase === 'failed')
    return (
      <SheetSection title={copy.title} testId="public-link">
        <p className="ws-sharesheet-help" role="alert">
          {copy.loadFailed}{' '}
          <SheetButton small onClick={() => setAttempt((value) => value + 1)}>
            {copy.retry}
          </SheetButton>
        </p>
      </SheetSection>
    );
  if (!state?.available) return null;
  const live = state.state === 'live' && state.url;
  const unpublish = (
    <Unpublish
      copy={copy}
      busy={busy}
      open={unpublishing}
      onOpen={setUnpublishing}
      onConfirm={() =>
        void run('unpublish', () => publicLinkApi.unpublish(selection.artifactId, session))
      }
    />
  );
  // Review M6: a company owner or admin can take any colleague's link down.
  if (!state.youOwnIt)
    return live ? (
      <SheetSection
        title={copy.title}
        testId="public-link"
        help={state.canUnpublish ? copy.adminLive : copy.viewerLive}
      >
        {error && (
          <p className="ws-public-error" role="alert" data-testid="public-link-error">
            {error}
          </p>
        )}
        <CopyField
          value={state.url!}
          label={copy.copyLink}
          copiedLabel={copy.copied}
          testId="public-link-url"
        />
        {state.canUnpublish && <div className="ws-public-row">{unpublish}</div>}
      </SheetSection>
    ) : null;
  if (state.state === 'taken_down')
    return (
      <SheetSection title={copy.title} testId="public-link">
        <SheetNote testId="public-link-taken-down">
          {copy.takenDown}
          {state.takenDown?.reason
            ? ` ${formatCopy(copy.takenDownReason, { reason: state.takenDown.reason })}`
            : ''}
        </SheetNote>
      </SheetSection>
    );
  const behind = live && state.selectedVersionId && state.versionId !== state.selectedVersionId;
  const publish = (
    <>
      <Exposes state={state} copy={copy} />
      <label className="ws-public-confirm">
        <input
          type="checkbox"
          checked={confirmed}
          disabled={busy !== null}
          data-testid="public-link-confirm"
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        <span>{copy.confirm}</span>
      </label>
      <div className="ws-public-row">
        <SheetButton
          primary
          disabled={!confirmed || busy !== null}
          data-testid="public-link-publish"
          onClick={() => void run('publish', () => publicLinkApi.publish(selection, session))}
        >
          <GlobeIcon aria-hidden="true" />
          {busy === 'publish' ? copy.publishing : behind ? copy.republish : copy.publish}
        </SheetButton>
      </div>
    </>
  );
  const sitesChanged = readSites(sites).join(' ') !== state.embedOrigins.join(' ');
  return (
    <SheetSection title={copy.title} testId="public-link" help={copy.help}>
      {error && (
        <p className="ws-public-error" role="alert" data-testid="public-link-error">
          {error}
        </p>
      )}
      {!live ? (
        <div className="ws-public">{publish}</div>
      ) : (
        <div className="ws-public" data-state="live">
          <div className="ws-public-head">
            <span className="ws-public-pill" data-testid="public-link-live">
              {copy.live}
            </span>
            {state.refreshedAt && (
              <span className="ws-public-meta">
                {formatCopy(copy.copiedAt, {
                  time: new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(state.refreshedAt)),
                })}
              </span>
            )}
          </div>
          <CopyField
            value={state.url!}
            label={copy.copyLink}
            copiedLabel={copy.copied}
            testId="public-link-url"
          >
            <a
              className="ws-sharesheet-button small"
              href={state.url!}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-link-open"
            >
              <ExternalLinkIcon aria-hidden="true" />
              {copy.open}
            </a>
          </CopyField>
          <div className="ws-public-row">
            <SheetButton
              small
              disabled={busy !== null || Boolean(behind)}
              data-testid="public-link-refresh"
              onClick={() =>
                void run('refresh', () => publicLinkApi.refresh(selection.artifactId, session))
              }
            >
              {busy === 'refresh' ? copy.refreshing : copy.refresh}
            </SheetButton>
            {unpublish}
          </div>
          <details className="ws-public-details">
            <summary>{copy.whatTitle}</summary>
            <Exposes state={state} copy={copy} titled={false} />
          </details>
          {behind && (
            <div className="ws-public-behind" data-testid="public-link-behind">
              <p className="ws-sharesheet-help">{copy.behind}</p>
              {publish}
            </div>
          )}
          <div className="ws-public-embed" data-testid="public-link-embed">
            <span className="ws-public-exposes-title">{copy.embedTitle}</span>
            <p className="ws-sharesheet-help">{copy.embedHelp}</p>
            <label className="ws-public-sites">
              <span>{copy.embedSites}</span>
              <input
                type="text"
                value={sites}
                placeholder={copy.embedSitesPlaceholder}
                spellCheck={false}
                autoComplete="off"
                disabled={busy !== null}
                data-testid="public-link-sites"
                onChange={(event) => setSites(event.target.value)}
              />
            </label>
            {sitesChanged && (
              <div className="ws-public-row">
                <SheetButton
                  small
                  disabled={busy !== null}
                  data-testid="public-link-sites-save"
                  onClick={() =>
                    void run('embed', () =>
                      publicLinkApi.embed(selection.artifactId, readSites(sites), session),
                    )
                  }
                >
                  {busy === 'embed' ? copy.saving : copy.saveSites}
                </SheetButton>
              </div>
            )}
            {state.embedOrigins.length && state.embedSnippet ? (
              <CopyField
                value={state.embedSnippet}
                label={copy.copyCode}
                copiedLabel={copy.copied}
                testId="public-link-snippet"
                multiline
              />
            ) : (
              <p className="ws-sharesheet-help">{copy.noSites}</p>
            )}
          </div>
        </div>
      )}
    </SheetSection>
  );
}
